/**
 * PersonaOfficeShell — shared office dashboard shell for L9/L10/L6/L5
 * personas (PR agency · Casting · Talent Management · Event Production ·
 * Studio Network). Renders a persona-branded hero + a tile grid driven
 * by config so every dashboard is a tiny wrapper (see the four thin
 * wrappers in /app/frontend/src/pages).
 *
 * The advertising_agency_admin persona still uses its bespoke
 * AgencyOffice.jsx (much richer — full desk registry + consult module).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { BadgeCheck, MapPin, ArrowRight, Globe, Users, Send } from "lucide-react";
import OrgTeamPanel from "@/components/OrgTeamPanel";

export default function PersonaOfficeShell({ config }) {
  const {
    layer, label, testidPrefix, accent, Icon, blurb,
    tiles, showSponsorQueue, expectedOrgType,
  } = config;

  const [org, setOrg] = useState(null);
  const [pending, setPending] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/onboard/me/organization");
        setOrg(data.organization);
      } catch {
        setOrg(null);
      }
      if (showSponsorQueue) {
        try {
          const { data } = await api.get("/pending-individuals/mine");
          setPending(data.count ?? 0);
        } catch {
          setPending(0);
        }
      }
    })();
  }, [showSponsorQueue]);

  if (!org) {
    return (
      <div className="mx-auto max-w-7xl px-6 py-16 text-center text-white/55"
           data-testid={`${testidPrefix}-loading`}>
        Loading dashboard…
      </div>
    );
  }

  // Guardrail — if a mistyped account_type routes here, tell the user.
  if (expectedOrgType && org.type !== expectedOrgType) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-16" data-testid={`${testidPrefix}-mismatch`}>
        <div className="cs-card rounded-2xl p-10 text-center">
          <Icon size={32} className="mx-auto text-white/40 mb-4" />
          <h1 className="font-display text-2xl uppercase tracking-tight">{label}</h1>
          <p className="mt-3 text-sm text-white/65">
            Your org is registered as &ldquo;{org.type}&rdquo;, not {label.toLowerCase()}.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10"
         data-testid={`${testidPrefix}-dashboard`}>
      {/* HERO */}
      <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">
        {layer} · {label}
      </div>
      <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2"
          style={{ color: accent }}
          data-testid={`${testidPrefix}-brand`}>
        {org.brand_name || org.legal_name}
      </h1>
      <div className="mt-2 text-sm text-white/55 flex items-center gap-3 flex-wrap">
        {org.legal_name && org.legal_name !== org.brand_name && <span>{org.legal_name}</span>}
        {org.verified && (
          <span className="px-2 py-0.5 rounded-full text-[10px] tracking-[0.18em] uppercase bg-emerald-500/15 text-emerald-300"
                data-testid={`${testidPrefix}-verified-chip`}>
            <BadgeCheck size={10} className="inline mr-1" /> Verified
          </span>
        )}
        {!org.verified && (
          <span className="px-2 py-0.5 rounded-full text-[10px] tracking-[0.18em] uppercase bg-amber-500/15 text-amber-300"
                data-testid={`${testidPrefix}-unverified-chip`}>
            KYC Pending
          </span>
        )}
        {org.registered_city && (
          <span className="px-2 py-0.5 rounded-full text-[10px] tracking-[0.18em] uppercase bg-white/8 text-white/65">
            <MapPin size={10} className="inline mr-1" /> {org.registered_city}
          </span>
        )}
      </div>

      {blurb && (
        <p className="mt-4 text-sm text-white/60 max-w-2xl leading-relaxed">
          {blurb}
        </p>
      )}

      {/* SPONSOR PENDING BANNER — only PR / Casting / Talent Mgmt */}
      {showSponsorQueue && (
        <Link
          to="/pending-individuals"
          data-testid={`${testidPrefix}-sponsor-pending`}
          className="mt-8 cs-card rounded-2xl p-5 flex items-center gap-5 hover:translate-y-[-2px] transition-all border border-emerald-500/25 bg-emerald-500/[0.04] block">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-emerald-500/15 text-emerald-300 shrink-0">
            <BadgeCheck size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] tracking-[0.3em] uppercase text-emerald-300/80">
              Verification Queue
            </div>
            <div className="font-display text-xl uppercase tracking-tight mt-1">
              Pending {config.sponsorLabel || "Verifications"}
            </div>
            <div className="text-xs text-white/60 mt-1">
              {pending === null
                ? "Loading…"
                : pending === 0
                ? "Nothing pending. Nominees appear here when they list you as sponsor."
                : `${pending} awaiting your decision.`}
            </div>
          </div>
          {pending > 0 && (
            <div className="px-3 py-1.5 rounded-full bg-emerald-500 text-black text-xs font-bold"
                 data-testid={`${testidPrefix}-sponsor-count`}>
              {pending}
            </div>
          )}
          <ArrowRight size={16} className="text-white/60 shrink-0" />
        </Link>
      )}

      {/* TILES */}
      <div className="mt-10">
        <div className="text-[10px] tracking-[0.28em] uppercase text-white/45 mb-3">
          Your Desks
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
             data-testid={`${testidPrefix}-tiles`}>
          {tiles.map((t) => (
            <TileCard key={t.key} tile={t} accent={accent}
                      testidPrefix={testidPrefix} />
          ))}
        </div>
      </div>

      {/* TEAM PANEL */}
      <div className="mt-10">
        <OrgTeamPanel org={org} />
      </div>
    </div>
  );
}

function TileCard({ tile, accent, testidPrefix }) {
  const { key, to, TileIcon, title, desc, chip, chipTone } = tile;
  const chipToneClass = chipTone === "live"
    ? "bg-emerald-500/20 text-emerald-200"
    : chipTone === "soon"
    ? "bg-white/8 text-white/55"
    : "bg-amber-500/20 text-amber-200";

  const Inner = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center"
             style={{ background: `${accent}22`, color: accent }}>
          <TileIcon size={18} />
        </div>
        {chip && (
          <span className={`px-2 py-0.5 rounded-full text-[9px] tracking-[0.2em] uppercase font-semibold ${chipToneClass}`}>
            {chip}
          </span>
        )}
      </div>
      <div className="mt-4">
        <div className="font-display text-lg uppercase tracking-tight">{title}</div>
        <p className="mt-1.5 text-[12px] text-white/60 leading-relaxed">{desc}</p>
      </div>
      <div className="mt-4 pt-4 border-t border-white/8 flex items-center justify-between">
        <span className="text-[10px] tracking-[0.22em] uppercase text-white/60 group-hover:text-white">
          {to ? "Open →" : "Coming Soon"}
        </span>
        {to && <ArrowRight size={13} className="text-white/40 group-hover:text-white group-hover:translate-x-0.5 transition" />}
      </div>
    </>
  );

  const cls = "cs-card rounded-xl p-5 hover:border-white/25 transition-all block group";
  const tid = `${testidPrefix}-tile-${key}`;
  return to ? (
    <Link to={to} className={cls} data-testid={tid}>{Inner}</Link>
  ) : (
    <div className={cls + " opacity-70 cursor-not-allowed"} data-testid={tid}>{Inner}</div>
  );
}

/* Re-export the two icons the pages compose in the tile lists so page
 * files stay import-light. */
export { Globe, Users, Send };
